/**
 * NFT Collection Contract Configuration
 * 
 * This file contains the contract ABI and address configuration
 */

// Contract addresses for different networks
const CONTRACT_ADDRESSES: Record<number, `0x${string}`> = {
  // Localhost (Hardhat) - for local development
  31337: (import.meta.env.VITE_CONTRACT_ADDRESS_LOCALHOST || '0xF786A88277B75B086F7e87139759341F8DE8d40e') as `0x${string}`,
  // Arbitrum One Mainnet
  42161: (import.meta.env.VITE_CONTRACT_ADDRESS_ARBITRUM || '0xF786A88277B75B086F7e87139759341F8DE8d40e') as `0x${string}`,
  // Polygon Mainnet
  137: (import.meta.env.VITE_CONTRACT_ADDRESS_POLYGON || '0xD46B9FAa61c8d5505c298dD466749aa8b6009A72') as `0x${string}`,
  // BSC Mainnet
  56: (import.meta.env.VITE_CONTRACT_ADDRESS_BSC || '0xAc1D5d5Fa7Af46b375856E3F5a6F420889030803') as `0x${string}`,
}

// Fallback to old env variable for backward compatibility
const FALLBACK_ADDRESS = (import.meta.env.VITE_CONTRACT_ADDRESS || '') as `0x${string}`

/**
 * Get contract address for a specific chain ID
 */
export const getContractAddress = (chainId?: number): `0x${string}` => {
  if (chainId && CONTRACT_ADDRESSES[chainId]) {
    return CONTRACT_ADDRESSES[chainId]
  }
  
  // Fallback to old env variable
  if (FALLBACK_ADDRESS && FALLBACK_ADDRESS !== '0x') {
    return FALLBACK_ADDRESS
  }
  
  // Default to localhost address
  return CONTRACT_ADDRESSES[31337]
}

// Default export for backward compatibility
export const CONTRACT_ADDRESS = FALLBACK_ADDRESS || CONTRACT_ADDRESSES[31337]

if (!CONTRACT_ADDRESS || CONTRACT_ADDRESS === '0x') {
  console.warn('⚠️ VITE_CONTRACT_ADDRESS is not configured. Using default localhost address.')
}

// NFT Collection Contract ABI (only the functions we need)
export const NFT_CONTRACT_ABI = [
  // Read Functions
  {
    inputs: [],
    name: 'owner',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'getCurrentPrice',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'totalMinted',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'MAX_SUPPLY',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'whitelistActive',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'revealed',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'getBaseTokenURI',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'paused',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: '_address', type: 'address' }],
    name: 'numberMinted',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'owner', type: 'address' }],
    name: 'balanceOf',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: '_owner', type: 'address' }],
    name: 'tokensOfOwner',
    outputs: [{ internalType: 'uint256[]', name: '', type: 'uint256[]' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: '_tokenId', type: 'uint256' }],
    name: 'checkRefundEligibility',
    outputs: [
      { internalType: 'bool', name: 'eligible', type: 'bool' },
      { internalType: 'uint256', name: 'refundAmount', type: 'uint256' },
      { internalType: 'uint256', name: 'timeLeft', type: 'uint256' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'tokenId', type: 'uint256' }],
    name: 'tokenURI',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'MAX_PER_WALLET',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'MAX_PER_TX',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'startPrice',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'endPrice',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'priceDecrement',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'merkleRoot',
    outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'royaltyReceiver',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'royaltyPercentage',
    outputs: [{ internalType: 'uint96', name: '', type: 'uint96' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'notRevealedURI',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'REFUND_PERIOD',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'auctionStartTime',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'merkleRoot',
    outputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'royaltyReceiver',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'royaltyPercentage',
    outputs: [{ internalType: 'uint96', name: '', type: 'uint96' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'notRevealedURI',
    outputs: [{ internalType: 'string', name: '', type: 'string' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [],
    name: 'REFUND_PERIOD',
    outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: '_address', type: 'address' },
      { internalType: 'bytes32[]', name: '_merkleProof', type: 'bytes32[]' },
    ],
    name: 'isWhitelisted',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },

  // Write Functions
  {
    inputs: [
      { internalType: 'uint256', name: '_quantity', type: 'uint256' },
      { internalType: 'bytes32[]', name: '_merkleProof', type: 'bytes32[]' },
    ],
    name: 'mint',
    outputs: [],
    stateMutability: 'payable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: '_tokenId', type: 'uint256' }],
    name: 'refundNFT',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },

  // Owner Functions
  {
    inputs: [{ internalType: 'address', name: 'newOwner', type: 'address' }],
    name: 'transferOwnership',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'pause',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'unpause',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'bytes32', name: 'newMerkleRoot', type: 'bytes32' }],
    name: 'setMerkleRoot',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'bool', name: '_active', type: 'bool' }],
    name: 'setWhitelistActive',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'string', name: '_baseURI', type: 'string' }],
    name: 'setBaseURI',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'string', name: '_notRevealedURI', type: 'string' }],
    name: 'setNotRevealedURI',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'string', name: '_baseURI', type: 'string' }],
    name: 'reveal',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'uint256', name: 'newStartPrice', type: 'uint256' },
      { internalType: 'uint256', name: 'newEndPrice', type: 'uint256' },
      { internalType: 'uint256', name: 'newPriceDecrement', type: 'uint256' },
    ],
    name: 'updateAuctionConfig',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'resetAuction',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'newRoyaltyReceiver', type: 'address' },
      { internalType: 'uint96', name: 'newRoyaltyPercentage', type: 'uint96' },
    ],
    name: 'setRoyaltyInfo',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [
      { internalType: 'address', name: 'to', type: 'address' },
      { internalType: 'uint256', name: 'quantity', type: 'uint256' },
    ],
    name: 'ownerMint',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'distributeRevenue',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [],
    name: 'emergencyWithdraw',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'newMaxSupply', type: 'uint256' }],
    name: 'setMaxSupply',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'newRefundPeriod', type: 'uint256' }],
    name: 'setRefundPeriod',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'newMaxPerWallet', type: 'uint256' }],
    name: 'setMaxPerWallet',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'newMaxPerTx', type: 'uint256' }],
    name: 'setMaxPerTx',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'newStartPrice', type: 'uint256' }],
    name: 'setStartPrice',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'newEndPrice', type: 'uint256' }],
    name: 'setEndPrice',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function',
  },

  // Events
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'minter', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'quantity', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'totalPrice', type: 'uint256' },
    ],
    name: 'Minted',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'refunder', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'tokenId', type: 'uint256' },
      { indexed: false, internalType: 'uint256', name: 'refundAmount', type: 'uint256' },
    ],
    name: 'Refunded',
    type: 'event',
  },
  {
    anonymous: false,
    inputs: [{ indexed: false, internalType: 'string', name: 'baseURI', type: 'string' }],
    name: 'Revealed',
    type: 'event',
  },

  // Custom Errors
  {
    inputs: [],
    name: 'ExceedsMaxSupply',
    type: 'error',
  },
  {
    inputs: [],
    name: 'ExceedsMaxPerWallet',
    type: 'error',
  },
  {
    inputs: [],
    name: 'ExceedsMaxPerTransaction',
    type: 'error',
  },
  {
    inputs: [],
    name: 'InsufficientPayment',
    type: 'error',
  },
  {
    inputs: [],
    name: 'InvalidProof',
    type: 'error',
  },
  {
    inputs: [],
    name: 'CooldownNotPassed',
    type: 'error',
  },
  {
    inputs: [],
    name: 'RefundPeriodExpired',
    type: 'error',
  },
  {
    inputs: [],
    name: 'TokenNotOwned',
    type: 'error',
  },
  {
    inputs: [],
    name: 'RefundAlreadyClaimed',
    type: 'error',
  },
] as const

/**
 * Get contract configuration for a specific chain ID
 * This ensures the correct contract address is used for each network
 */
export const getNFTContract = (chainId?: number) => ({
  address: getContractAddress(chainId),
  abi: NFT_CONTRACT_ABI,
} as const)

// Default contract configuration (for backward compatibility)
// Will use localhost address by default
export const NFT_CONTRACT = {
  address: CONTRACT_ADDRESS,
  abi: NFT_CONTRACT_ABI,
} as const

// Type-safe contract errors
export const CONTRACT_ERRORS = {
  EXCEEDS_MAX_SUPPLY: 'ExceedsMaxSupply',
  EXCEEDS_MAX_PER_WALLET: 'ExceedsMaxPerWallet',
  EXCEEDS_MAX_PER_TRANSACTION: 'ExceedsMaxPerTransaction',
  INSUFFICIENT_PAYMENT: 'InsufficientPayment',
  INVALID_PROOF: 'InvalidProof',
  COOLDOWN_NOT_PASSED: 'CooldownNotPassed',
  REFUND_PERIOD_EXPIRED: 'RefundPeriodExpired',
  TOKEN_NOT_OWNED: 'TokenNotOwned',
  REFUND_ALREADY_CLAIMED: 'RefundAlreadyClaimed',
} as const

// Helper to parse contract errors
export const parseContractError = (error: Error): string => {
  const errorMessage = error.message.toLowerCase()

  if (errorMessage.includes('exceeds max supply') || errorMessage.includes('exceedsmaxsupply')) {
    return 'Collection is sold out!'
  }
  if (errorMessage.includes('exceeds max per wallet') || errorMessage.includes('exceedsmaxperwallet')) {
    return 'You have reached the maximum mints per wallet (10 NFTs)'
  }
  if (errorMessage.includes('exceeds max per transaction') || errorMessage.includes('exceedsmaximumpertransaction')) {
    return 'Maximum 5 NFTs per transaction'
  }
  if (errorMessage.includes('insufficient payment') || errorMessage.includes('insufficientpayment')) {
    return 'Insufficient payment sent'
  }
  if (errorMessage.includes('invalid proof') || errorMessage.includes('invalidproof')) {
    return 'You are not on the whitelist'
  }
  if (errorMessage.includes('cooldown') || errorMessage.includes('cooldownnotpassed')) {
    return 'Please wait 5 minutes between mints'
  }
  if (errorMessage.includes('refund period expired') || errorMessage.includes('refundperiodexpired')) {
    return 'Refund period has expired (7 days)'
  }
  if (errorMessage.includes('token not owned') || errorMessage.includes('tokennotowned')) {
    return 'You do not own this token'
  }
  if (errorMessage.includes('refund already claimed') || errorMessage.includes('refundalreadyclaimed')) {
    return 'Refund has already been claimed for this token'
  }
  if (errorMessage.includes('user rejected') || errorMessage.includes('user denied')) {
    return 'Transaction was rejected'
  }
  if (errorMessage.includes('insufficient funds')) {
    return 'Insufficient funds in wallet'
  }
  if (errorMessage.includes('gas limit too high')) {
    return 'Gas limit is too high for this network. In MetaMask, open “Edit gas limit” and set a smaller value (for example 300000–500000), then confirm the transaction again.'
  }

  return 'Transaction failed. Please try again.'
}
